// SellerActivityPopup.jsx
// Shows the seller one card per ACTIVE activity (status === true) on their
// properties (property_by === logged-in user_id).
//   X          -> sets that record's status to false
//   Close All  -> sets status to false on every record shown
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, IconButton, Button } from '@mui/material';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';

import { BASE_URL } from '../../Api/ApiUrls';
import { AuthContext } from '../../AuthContext/AuthContext';

const TITLE_DARK = '#3B0A0A';
const BROWN = '#7A3E1D';
const SUBTLE = '#6F6258';
const ICON_BG = '#FDEFE3';

// activity_type -> popup content ({who} is replaced with the customer name)
const CONFIG = {
  Call: {
    title: 'Contact Details Viewed',
    message: '{who} viewed your contact number.',
    Icon: PhoneRoundedIcon,
  },
  Location: {
    title: 'Property Location Viewed',
    message: '{who} viewed your property location on map.',
    Icon: LocationOnRoundedIcon,
  },
  Chat: {
    title: 'New Chat',
    message: '{who} started a chat with you on Property Pincoder.',
    Icon: ChatBubbleRoundedIcon,
  },
  Liked: {
    title: 'Added to Wishlist',
    message: '{who} added your property to their Wishlist.',
    Icon: FavoriteRoundedIcon,
  },
  Description: {
    title: 'Property Viewed',
    message: '{who} viewed your property details.',
    Icon: VisibilityRoundedIcon,
  },
};

// Which property this record is about (normal / ad / bank auction)
const propertyRef = (a) =>
  a.property_id ?? a.ad_property ?? a.bank_auction_property ?? null;

const SellerActivityPopup = () => {
  const { userId } = useContext(AuthContext);
  const [items, setItems] = useState([]); // [{ record, name }]

  // X on one card -> PUT /activities/{log_id}/  { seen_status: false }
  const markOneSeen = async (logId) => {
    try {
      await axios.put(`${BASE_URL}/activities/${logId}/`, { seen_status: false });
    } catch (err) {
      console.error(`Failed to update activity ${logId}:`, err);
    }
  };

  // Close All -> POST /activities/  { log_ids: [...], seen_status: false }
  const markManySeen = async (logIds) => {
    try {
      await axios.put(`${BASE_URL}/activities/`, {
        log_ids: logIds,
        seen_status: false,
      });
    } catch (err) {
      console.error('Failed to bulk update activities:', err);
    }
  };

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      try {
        // API is filtered by property_by (logged-in user id)
        const res = await axios.get(`${BASE_URL}/activities/`, {
          params: { property_by: userId },
        });

        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];

        // ONLY records where seen_status === true
        const mine = list
          .filter((a) => a.seen_status === true && CONFIG[a.activity_type])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        if (mine.length === 0) return;

        // customer names
        const ids = [...new Set(mine.map((a) => a.user_id))];
        const nameMap = {};
        await Promise.all(
          ids.map(async (id) => {
            try {
              const u = await axios.get(`${BASE_URL}/users/${id}/`);
              nameMap[id] = u.data?.username || 'A customer';
            } catch {
              nameMap[id] = 'A customer';
            }
          })
        );

        setItems(mine.map((record) => ({ record, name: nameMap[record.user_id] })));
      } catch (err) {
        console.error('Error loading seller activity:', err);
      }
    };

    load();
  }, [userId]);

  // X on one card
  const dismissOne = (logId) => {
    setItems((prev) => prev.filter((x) => x.record.log_id !== logId));
    markOneSeen(logId);
  };

  // Close All
  const closeAll = () => {
    const ids = items.map((x) => x.record.log_id);
    setItems([]);
    markManySeen(ids);
  };

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        bgcolor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 1.5,
        '@keyframes popIn': {
          from: { opacity: 0, transform: 'translateY(14px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          maxHeight: '72vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {items.map(({ record, name }, i) => {
          const cfg = CONFIG[record.activity_type];
          const { Icon } = cfg;
          const ref = propertyRef(record);

          return (
            <Box
              key={record.log_id}
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: '16px',
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                p: { xs: 1.2, sm: 1.6 },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.2, sm: 1.6 },
                flexShrink: 0,
                animation: `popIn 0.3s ease ${Math.min(i, 6) * 0.06}s both`,
              }}
            >
              <Box
                sx={{
                  width: { xs: 44, sm: 54 },
                  height: { xs: 44, sm: 54 },
                  borderRadius: '50%',
                  bgcolor: ICON_BG,
                  color: BROWN,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: { xs: 22, sm: 28 } }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.92rem', sm: '1.05rem' },
                    color: TITLE_DARK,
                    lineHeight: 1.2,
                  }}
                >
                  {cfg.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.74rem', sm: '0.84rem' },
                    color: SUBTLE,
                    mt: 0.3,
                    lineHeight: 1.35,
                    wordBreak: 'break-word',
                  }}
                >
                  {cfg.message.replace('{who}', name)}
                  {ref ? ` (Property ID: ${ref})` : ''}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={() => dismissOne(record.log_id)}
                sx={{ alignSelf: 'flex-start', color: '#444' }}
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Button
        onClick={closeAll}
        startIcon={<CloseRoundedIcon />}
        sx={{
          mt: 1.5,
          width: '100%',
          maxWidth: 420,
          py: 1.1,
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '1rem',
          color: '#fff',
          bgcolor: '#8B5A2B',
          border: '2px solid #fff',
          '&:hover': { bgcolor: '#7A4E24' },
        }}
      >
        Close All ({items.length})
      </Button>
    </Box>
  );
};

export default SellerActivityPopup;